import { useState, useEffect } from "react";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
  AlertDialogTrigger,
} from "@/components/ui/alert-dialog";
import { Loader2, Plus, Trash2, ToggleLeft, ToggleRight, Handshake } from "lucide-react";
import { supabase } from "@/integrations/supabase/client";
import { useToast } from "@/hooks/use-toast";

interface HandlingRelationship {
  id: string;
  organisation_id: string;
  handled_code: string;
  handled_name: string;
  is_active: boolean;
  created_at: string;
  org_name?: string;
  org_code?: string;
}

interface Organization {
  id: string;
  code: string;
  name: string;
}

export default function GroundHandlingPage() {
  const [relationships, setRelationships] = useState<HandlingRelationship[]>([]);
  const [organizations, setOrganizations] = useState<Organization[]>([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [showForm, setShowForm] = useState(false);
  const [selectedOrgId, setSelectedOrgId] = useState("");
  const [handledCode, setHandledCode] = useState("");
  const [handledName, setHandledName] = useState("");
  const { toast } = useToast();

  const fetchData = async () => {
    setLoading(true);
    const [relResult, orgResult] = await Promise.all([
      supabase
        .from("organisation_handling_relationships")
        .select("*")
        .order("created_at", { ascending: false }),
      supabase
        .from("organizations")
        .select("id, code, name")
        .eq("is_active", true)
        .order("name"),
    ]);

    const orgs = orgResult.data || [];
    setOrganizations(orgs);

    const orgMap = new Map(orgs.map((o) => [o.id, o]));
    const rels: HandlingRelationship[] = (relResult.data || []).map((r: any) => ({
      ...r,
      org_name: orgMap.get(r.organisation_id)?.name || "Unknown",
      org_code: orgMap.get(r.organisation_id)?.code || "?",
    }));
    setRelationships(rels);
    setLoading(false);
  };

  useEffect(() => {
    fetchData();
  }, []);

  const handleSave = async () => {
    if (!selectedOrgId || !handledCode.trim() || !handledName.trim()) {
      toast({ title: "Missing fields", description: "All fields are required.", variant: "destructive" });
      return;
    }

    setSaving(true);
    const { error } = await supabase.from("organisation_handling_relationships").insert({
      organisation_id: selectedOrgId,
      handled_code: handledCode.trim().toUpperCase(),
      handled_name: handledName.trim(),
      is_active: true,
    } as any);

    setSaving(false);

    if (error) {
      toast({ title: "Failed to save", description: error.message, variant: "destructive" });
    } else {
      toast({ title: "Relationship added" });
      setShowForm(false);
      setSelectedOrgId("");
      setHandledCode("");
      setHandledName("");
      fetchData();
    }
  };

  const toggleActive = async (id: string, currentlyActive: boolean) => {
    const { error } = await supabase
      .from("organisation_handling_relationships")
      .update({ is_active: !currentlyActive } as any)
      .eq("id", id);

    if (error) {
      toast({ title: "Update failed", description: error.message, variant: "destructive" });
    } else {
      toast({ title: currentlyActive ? "Deactivated" : "Activated" });
      fetchData();
    }
  };

  const deleteRelationship = async (id: string) => {
    const { error } = await supabase
      .from("organisation_handling_relationships")
      .delete()
      .eq("id", id);

    if (error) {
      toast({ title: "Delete failed", description: error.message, variant: "destructive" });
    } else {
      toast({ title: "Relationship deleted" });
      fetchData();
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center py-12">
        <Loader2 className="h-8 w-8 animate-spin text-muted-foreground" />
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <Card>
        <CardHeader className="flex flex-row items-center justify-between">
          <CardTitle className="flex items-center gap-2">
            <Handshake className="h-5 w-5" />
            Ground Handling Relationships
          </CardTitle>
          <Button onClick={() => setShowForm(!showForm)} size="sm">
            <Plus className="h-4 w-4 mr-1" />
            Add Relationship
          </Button>
        </CardHeader>
        <CardContent className="space-y-4">
          {/* Inline Add Form */}
          {showForm && (
            <div className="rounded-lg border p-4 space-y-3 bg-muted/30">
              <div className="grid grid-cols-3 gap-3">
                <div className="space-y-1">
                  <Label className="text-xs">Handling Organisation</Label>
                  <Select value={selectedOrgId} onValueChange={setSelectedOrgId}>
                    <SelectTrigger>
                      <SelectValue placeholder="Select organisation" />
                    </SelectTrigger>
                    <SelectContent>
                      {organizations.map((org) => (
                        <SelectItem key={org.id} value={org.id}>
                          {org.code} — {org.name}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                <div className="space-y-1">
                  <Label className="text-xs">Handled IATA Code</Label>
                  <Input
                    value={handledCode}
                    onChange={(e) => setHandledCode(e.target.value)}
                    placeholder="e.g. QF"
                    maxLength={3}
                  />
                </div>
                <div className="space-y-1">
                  <Label className="text-xs">Handled Airline Name</Label>
                  <Input
                    value={handledName}
                    onChange={(e) => setHandledName(e.target.value)}
                    placeholder="e.g. Qantas"
                  />
                </div>
              </div>
              <div className="flex gap-2">
                <Button onClick={handleSave} disabled={saving} size="sm">
                  {saving ? <Loader2 className="h-4 w-4 animate-spin mr-1" /> : null}
                  Save
                </Button>
                <Button variant="outline" size="sm" onClick={() => setShowForm(false)}>
                  Cancel
                </Button>
              </div>
            </div>
          )}

          {/* Table */}
          {relationships.length === 0 ? (
            <p className="text-sm text-muted-foreground text-center py-8">
              No ground handling relationships configured yet.
            </p>
          ) : (
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Handling Airline</TableHead>
                  <TableHead>Handled Code</TableHead>
                  <TableHead>Handled Airline Name</TableHead>
                  <TableHead>Status</TableHead>
                  <TableHead className="text-right">Actions</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {relationships.map((rel) => (
                  <TableRow key={rel.id}>
                    <TableCell className="font-medium">
                      {rel.org_code} — {rel.org_name}
                    </TableCell>
                    <TableCell className="font-mono">{rel.handled_code}</TableCell>
                    <TableCell>{rel.handled_name}</TableCell>
                    <TableCell>
                      {rel.is_active ? (
                        <Badge className="bg-green-600 text-white">Active</Badge>
                      ) : (
                        <Badge variant="secondary">Inactive</Badge>
                      )}
                    </TableCell>
                    <TableCell className="text-right">
                      <div className="flex items-center justify-end gap-2">
                        <Button
                          variant="outline"
                          size="sm"
                          onClick={() => toggleActive(rel.id, rel.is_active ?? true)}
                          title={rel.is_active ? "Deactivate" : "Activate"}
                        >
                          {rel.is_active ? (
                            <ToggleRight className="h-4 w-4 text-green-600" />
                          ) : (
                            <ToggleLeft className="h-4 w-4" />
                          )}
                        </Button>
                        <AlertDialog>
                          <AlertDialogTrigger asChild>
                            <Button variant="outline" size="sm" title="Delete">
                              <Trash2 className="h-4 w-4 text-destructive" />
                            </Button>
                          </AlertDialogTrigger>
                          <AlertDialogContent>
                            <AlertDialogHeader>
                              <AlertDialogTitle>Delete relationship?</AlertDialogTitle>
                              <AlertDialogDescription>
                                Remove the handling relationship for {rel.handled_code} ({rel.handled_name}) 
                                from {rel.org_name}? This cannot be undone.
                              </AlertDialogDescription>
                            </AlertDialogHeader>
                            <AlertDialogFooter>
                              <AlertDialogCancel>Cancel</AlertDialogCancel>
                              <AlertDialogAction onClick={() => deleteRelationship(rel.id)}>
                                Delete
                              </AlertDialogAction>
                            </AlertDialogFooter>
                          </AlertDialogContent>
                        </AlertDialog>
                      </div>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          )}
        </CardContent>
      </Card>
    </div>
  );
}
